import axios from "axios";

/**
 * Resolve the API base URL.
 *
 * Normally we use REACT_APP_BACKEND_URL — but on deployed environments the
 * baked-in env value can be stale (e.g. a production frontend at smarterp-st.com
 * was built with the preview URL). Cross-origin + cookies + ACA-Origin: *
 * is incompatible, so the browser silently blocks every credentialed request
 * and axios surfaces it as a "Network connection failed".
 *
 * Fix: if the build-time URL doesn't match the current window.location.origin,
 * fall back to same-origin (which works because the deployment serves /api on
 * the same domain). Preview & dev are unaffected because the two URLs match.
 */
function resolveApiBase() {
  const envUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  if (origin && envUrl && origin !== envUrl) {
    return origin;
  }
  return envUrl || origin;
}

export const API_BASE = resolveApiBase();
const API = `${API_BASE}/api`;

const client = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
  timeout: 90000,  // 90s — fail fast before ingress proxy 504s with HTML
});

export function formatApiError(detail, fallbackContext) {
  if (detail == null) {
    // Surface the HTTP status / network error so we can diagnose what actually happened
    const status = fallbackContext?.status;
    if (status === 504) return "Server timed out. Please try again.";
    if (status === 502) return "Server unavailable (502). Please try again in a moment.";
    if (status === 503) return "Service temporarily unavailable. Please try again.";
    if (status === 429) return "Too many attempts. Please wait a minute and retry.";
    if (status === 401) return "Not authenticated. Please log in again.";
    if (status === 403) return "Not authorized for this action.";
    if (status) return `Request failed (HTTP ${status}). Please try again.`;
    if (fallbackContext?.networkError) return "Network connection failed. Check your internet and retry.";
    return "Something went wrong. Please try again.";
  }
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Helper: build the context object call-sites pass to formatApiError
export function errContext(err) {
  return {
    status: err?.response?.status,
    networkError: !err?.response,  // axios sets response=undefined on pure network failures
  };
}

export { client, API };
export default client;
